import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Authenticated, AuthLoading, Unauthenticated, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import Layout from "@/components/Layout";
import BatchUploadDialog from "@/components/BatchUploadDialog";
import FileDropProvider from "@/components/FileDropOverlay";
import RecorderProvider from "@/recorder/RecorderProvider";
import RecoveryBanner from "@/recorder/RecoveryBanner";
import SignIn from "@/routes/SignIn";
import Calendar from "@/routes/Calendar";
import Lesson from "@/routes/Lesson";
import Schedule from "@/routes/Schedule";
import Settings from "@/routes/Settings";
import Unassigned from "@/routes/Unassigned";

/** Seeds the settings singleton on first authenticated load. Idempotent. */
function SettingsBootstrap() {
  const ensure = useMutation(api.settings.ensure);
  useEffect(() => {
    void ensure({}).catch((error: unknown) => {
      console.error("Failed to initialize settings", error);
    });
  }, [ensure]);
  return null;
}

export default function App() {
  return (
    <>
      <AuthLoading>
        <div className="grid h-full place-items-center bg-[#fdfdfc] dark:bg-[#0f0f0e] font-mono text-xs text-neutral-400">
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-neutral-400 animate-pulse" />
            <span className="tracking-widest uppercase">Initializing…</span>
          </div>
        </div>
      </AuthLoading>

      <Unauthenticated>
        <SignIn />
      </Unauthenticated>

      <Authenticated>
        <SettingsBootstrap />
        {/* Above the router on purpose: one microphone, one recorder, and
            navigating away from the calendar must not stop a lecture. */}
        <RecorderProvider>
          <FileDropProvider>
            <Layout>
              <RecoveryBanner />
              <Routes>
                <Route path="/" element={<Calendar />} />
                <Route path="/lesson/:lessonId" element={<Lesson />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/unassigned" element={<Unassigned />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </FileDropProvider>
          <BatchUploadDialog />
        </RecorderProvider>
      </Authenticated>
    </>
  );
}
